import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

import { ClientLookbackSelector } from "../_components/ClientLookbackSelector";
import { ExchangeSelector } from "../_components/ExchangeSelector";
import { OpportunitiesTableClient } from "../_components/OpportunitiesTable";
import {
  useFundings,
  useMarkets,
  useOpportunities,
  useSpreads,
} from "../hooks/api";
import { useLocalStorage } from "../hooks/useLocalStorage";

const opportunitiesSearchSchema = z.object({
  days: z.number().optional(),
});

export const Route = createFileRoute("/opportunities")({
  validateSearch: opportunitiesSearchSchema,
  component: OpportunitiesPage,
});

function LoadingSkeleton() {
  return (
    <div className="overflow-hidden rounded-lg border border-edge bg-surface-1">
      <div className="border-b border-edge px-3 py-3">
        <div className="h-7 w-full max-w-xs animate-pulse rounded-md bg-surface-2" />
      </div>
      <div className="flex flex-col gap-2 p-3">
        {Array.from({ length: 12 }).map((_, i) => (
          <div
            key={i}
            className="h-9 animate-pulse rounded bg-surface-2"
            style={{ animationDelay: `${i * 60}ms` }}
          />
        ))}
      </div>
    </div>
  );
}

function UpdatedAt({ label, timestamp }: { label: string; timestamp?: string }) {
  if (!timestamp) return null;
  return (
    <span className="whitespace-nowrap">
      <span className="text-zinc-500">{label}</span>{" "}
      <span className="tabular-nums">
        {new Date(timestamp).toLocaleString()}
      </span>
    </span>
  );
}

function OpportunitiesPage() {
  const { days: daysParam } = Route.useSearch();
  const initialDays = daysParam || 1;

  const [days, setDays, daysLoaded] = useLocalStorage(
    "opportunities-lookback-days",
    initialDays,
  );
  const [exchanges, setExchanges, exchangesLoaded] = useLocalStorage<string[]>(
    "opportunities-exchanges",
    ["BINANCE", "BYBIT"],
  );

  const isLoaded = daysLoaded && exchangesLoaded;

  const { data: opportunities, error } = useOpportunities(days);
  const { data: spreads } = useSpreads();
  const { data: markets } = useMarkets();
  const { data: funding } = useFundings();

  return (
    <main className="min-h-screen bg-surface-0 text-zinc-100">
      <div className="mx-auto max-w-screen-2xl px-2 py-4 md:px-6 md:py-6">
        <header className="mb-4">
          <h1 className="text-lg font-semibold tracking-tight md:text-xl">
            Funding Rate Opportunities
          </h1>
          <p className="text-xs text-zinc-500">
            Cross-exchange perpetual funding arbitrage screener
          </p>
        </header>

        {error && (
          <div className="mb-3 rounded-md border border-neg/30 bg-neg/10 px-3 py-2 text-sm text-neg">
            Failed to load opportunities. Please try again later.
          </div>
        )}

        {!isLoaded || opportunities === undefined ? (
          <LoadingSkeleton />
        ) : (
          <OpportunitiesTableClient
            opportunities={opportunities.opportunities}
            exchanges={exchanges}
            toolbar={
              <>
                <ExchangeSelector
                  exchanges={exchanges}
                  setExchanges={setExchanges}
                />
                <ClientLookbackSelector days={days} onDaysChange={setDays} />
              </>
            }
          />
        )}

        <footer className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-zinc-600">
          <span className="font-medium tracking-wider text-zinc-500 uppercase">
            Updated
          </span>
          <UpdatedAt label="Funding" timestamp={funding?.updatedAt} />
          <UpdatedAt label="Spreads" timestamp={spreads?.updatedAt} />
          <UpdatedAt label="Markets" timestamp={markets?.updatedAt} />
          <UpdatedAt label="Historical" timestamp={opportunities?.updatedAt} />
        </footer>
      </div>
    </main>
  );
}
